import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Badge,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Link,
} from '@chakra-ui/react';
import { FaArrowRight, FaBuilding, FaIndustry, FaShoppingCart, FaHospital, FaGraduationCap, FaLandmark } from 'react-icons/fa';

const Cases: React.FC = () => {
  const bgGradient = useColorModeValue(
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(124,58,237,0.05) 100%)',
    'linear-gradient(180deg, rgba(26,32,44,0) 0%, rgba(124,58,237,0.1) 100%)'
  );

  return (
    <Box py={20} pt="160px" position="relative" overflow="hidden" bgGradient={bgGradient}>
      <Container maxW="1200px" position="relative" zIndex={1}>
        <Box textAlign="center" mb={16}>
          <Badge
            colorScheme="purple"
            mb={4}
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
          >
            客户案例展示
          </Badge>
          <Heading
            as="h1"
            size={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            mb={6}
            bgGradient="linear(to-r, brand.purple, purple.500)"
            bgClip="text"
          >
            成功案例
          </Heading>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="800px"
            mx="auto"
            mb={12}
          >
            我们已成功为金融、制造、零售、医疗等多个行业的企业提供AI解决方案，创造显著商业价值
          </Text>
        </Box>

        {/* 行业筛选标签 */}
        <HStack spacing={4} justifyContent="center" mb={12} flexWrap="wrap">
          {industries.map((industry, index) => (
            <Button
              key={index}
              leftIcon={<Icon as={industry.icon} />}
              variant="outline"
              colorScheme="purple"
              mb={2}
              size="md"
              px={6}
              _hover={{
                bg: "brand.purple",
                color: "white",
                transform: "translateY(-2px)"
              }}
              transition="all 0.3s ease"
            >
              {industry.name}
            </Button>
          ))}
        </HStack>

        {/* 案例卡片 */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
          {caseStudies.map((caseStudy, index) => (
            <Box
              key={index}
              borderRadius="xl"
              overflow="hidden"
              bg="white"
              boxShadow="0 4px 20px rgba(0,0,0,0.05)"
              _hover={{
                transform: "translateY(-8px)",
                boxShadow: "0 15px 30px rgba(124,58,237,0.1)"
              }}
              transition="all 0.3s ease"
            >
              <Box position="relative">
                <Image
                  src={caseStudy.imageSrc}
                  alt={caseStudy.title}
                  w="100%"
                  h="200px"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={4}
                  left={4}
                  colorScheme="purple"
                  px={3}
                  py={1}
                >
                  {caseStudy.industry}
                </Badge>
              </Box>

              <VStack align="start" p={6} spacing={4}>
                <Heading as="h3" size="md" color="brand.purple">
                  {caseStudy.title}
                </Heading>

                <Text color="gray.600" noOfLines={3}>
                  {caseStudy.description}
                </Text>

                <HStack>
                  {caseStudy.tags.map((tag, idx) => (
                    <Badge key={idx} colorScheme="purple" variant="subtle">
                      {tag}
                    </Badge>
                  ))}
                </HStack>

                <HStack justifyContent="space-between" w="100%">
                  <Text fontWeight="bold" color="gray.500">
                    {caseStudy.client}
                  </Text>
                  <Link
                    color="brand.purple"
                    fontWeight="semibold"
                    display="flex"
                    alignItems="center"
                    _hover={{ textDecoration: "none" }}
                  >
                    详情 <Icon as={FaArrowRight} ml={1} boxSize={3} />
                  </Link>
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>

        {/* 更多案例按钮 */}
        <Flex justifyContent="center" mt={12}>
          <Button
            size="lg"
            colorScheme="purple"
            px={8}
            rightIcon={<FaArrowRight />}
            bgGradient="linear(to-r, brand.purple, purple.600)"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 10px 15px -3px rgba(124,58,237,0.3)"
            }}
          >
            查看更多案例
          </Button>
        </Flex>
      </Container>
    </Box>
  );
};

const industries = [
  { name: '全部行业', icon: FaBuilding },
  { name: '金融服务', icon: FaLandmark },
  { name: '制造工业', icon: FaIndustry },
  { name: '零售商业', icon: FaShoppingCart },
  { name: '医疗健康', icon: FaHospital },
  { name: '教育科研', icon: FaGraduationCap },
];

const caseStudies = [
  {
    title: '企业智能知识库平台',
    description: '为某国有大型金融机构构建面向员工的智能知识库系统，整合内部规章制度、产品手册和专业知识，实现智能检索和问答功能。系统采用先进NLP技术，支持语义搜索，将信息查找时间缩短85%，新员工培训效率提升60%，企业知识传承更加系统化。',
    client: '某国有大型金融机构',
    imageSrc: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '金融服务',
    tags: ['智能知识库', 'NLP', '企业培训']
  },
  {
    title: '工业产品缺陷检测系统',
    description: '为某电子制造企业开发基于计算机视觉的产品缺陷检测系统，通过深度学习算法实时分析产品质量，识别微小瑕疵。系统支持多类型缺陷识别，准确率达99.2%，减少人工检测成本65%，产品退货率降低42%，显著提升产品质量和客户满意度。',
    client: '某电子制造企业',
    imageSrc: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '制造工业',
    tags: ['计算机视觉', '缺陷检测', '质量控制']
  },
  {
    title: '智能数据分析平台',
    description: '为某互联网企业打造全方位数据分析平台，整合多渠道数据源，构建可视化分析工具和预测模型。通过AI驱动的数据洞察，协助企业发现潜在商机和用户需求，客户转化率提升28%，营销投入产出比提高45%，为业务增长提供强有力的数据支持。',
    client: '某互联网企业',
    imageSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '互联网',
    tags: ['数据分析', '可视化', '精准营销']
  },
  {
    title: '医疗影像智能诊断助手',
    description: '为三甲医院开发医疗影像辅助诊断系统，运用深度学习技术辅助医生分析CT、核磁共振等影像，提高肺结节、脑出血等疾病的诊断准确率。系统可自动标记可疑区域，提高诊断准确率15%，缩短诊断时间40%，显著提升医院诊断效率和医疗服务水平。',
    client: '某三甲医院',
    imageSrc: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '医疗健康',
    tags: ['医疗影像', '深度学习', '辅助诊断']
  },
  {
    title: '智慧教育个性化学习系统',
    description: '为某教育集团开发个性化学习内容推荐系统，基于学生学习行为和知识图谱，智能分析学习薄弱环节，推荐适合的学习资料和练习题。系统采用自适应学习算法，学生学习效率提升30%，知识掌握程度提高25%，平台活跃度增长40%。',
    client: '某全国教育集团',
    imageSrc: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '教育科研',
    tags: ['个性化推荐', '知识图谱', '自适应学习']
  },
  {
    title: '智能风控决策系统',
    description: '为某金融科技公司开发智能风控决策系统，整合多维数据和机器学习模型，实现秒级信贷审批和风险预警。系统支持多场景风控策略，欺诈识别准确率达95%，不良贷款率下降20%，审批效率提升500%，每年为企业节约风险成本数千万元。',
    client: '某金融科技公司',
    imageSrc: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&auto=format&fit=crop',
    industry: '金融服务',
    tags: ['风险控制', '机器学习', '实时决策']
  },
];

export default Cases;